import cloneDeep from "lodash/cloneDeep";
import every from "lodash/every";
import filter from "lodash/filter";
import find from "lodash/find";
import map from "lodash/map";
import remove from "lodash/remove";
import { v1 as uuid } from "uuid";
import { SelectionPhoto } from "../../types/selection/selection";

const CONCURRENT_UPLOADS = 2;

export interface UploadItem {
  file?: File;
  key: string;
  reject: UploadErrorCallback;
  selectionPhoto: SelectionPhoto;
  status: "queued" | "started" | "finished";
  success: UploadSuccessCallback;
}

export type UploadSuccessCallback = (selectionPhoto: SelectionPhoto) => void;
export type UploadErrorCallback = (selectionPhoto: SelectionPhoto) => void;

export type OnTickCallback = () => void;
export type OnQueueEmptyCallback = (selectionPhotos: SelectionPhoto[]) => void;

export default class BaseUploadProvider {
  queue: UploadItem[];

  // Tick Event

  onTickSubscribers: Array<{ callback: OnTickCallback }> = [];

  // QueueEmpty Event

  onQueueEmptySubscribers: Array<{ callback: OnQueueEmptyCallback }> = [];

  constructor() {
    this.queue = [];
  }

  async upload(selectionPhoto: SelectionPhoto, file?: File): Promise<SelectionPhoto> {
    const key = uuid();
    return new Promise((success: UploadSuccessCallback, reject: UploadErrorCallback) => {
      this.queue.push({
        file,
        key,
        reject,
        selectionPhoto: cloneDeep(selectionPhoto), // Dup, as we'll be modifying directly
        status: "queued",
        success,
      });

      this.tick();
    });
  }

  tick(): void {
    setTimeout(() => this.doTick(), 100);
  }

  doTick(): void {
    if (every(this.queue, ({ status }) => status === "finished")) {
      const selectionPhotos = map(this.queue, ({ selectionPhoto }) => selectionPhoto);
      this.queue.splice(0);
      this.notifyOnQueueEmpty(selectionPhotos);
    }

    const uploadingCount = filter(this.queue, ({ status }) => status === "started").length;
    if (uploadingCount < CONCURRENT_UPLOADS) {
      const candidatItem = find(this.queue, ({ status }) => status === "queued");
      if (candidatItem) {
        this.start(candidatItem);
      }
    }

    this.notifyOnTick();
  }

  start(uploadItem: UploadItem): void {
    uploadItem.status = "started";
    this.doUpload(uploadItem)
      .then(() => {
        this.finish(uploadItem);
      })
      .catch(() => {
        this.reject(uploadItem);
      });
  }

  finish(uploadItem: UploadItem): void {
    uploadItem.status = "finished";
    uploadItem.success(uploadItem.selectionPhoto);
    this.tick();
  }

  reject(uploadItem: UploadItem): void {
    uploadItem.status = "finished";
    uploadItem.reject(uploadItem.selectionPhoto);
    this.tick();
  }

  doUpload(uploadItem: UploadItem): Promise<void> {
    throw new Error(`Not implemented, itemKey: ${uploadItem.key}`);
  }

  count(status?: "queued" | "started" | "finished"): number {
    if (!status) {
      return this.queue.length;
    }

    return filter(this.queue, (item) => item.status === status).length;
  }

  onTick(callback: OnTickCallback): () => void {
    const subscriber = { callback };
    this.onTickSubscribers.push(subscriber);

    return () => {
      remove(this.onTickSubscribers, subscriber);
    };
  }

  notifyOnTick(): void {
    for (const subscriber of this.onTickSubscribers) {
      setTimeout(() => subscriber.callback(), 0);
    }
  }

  onQueueEmpty(callback: OnQueueEmptyCallback): () => void {
    const subscriber = { callback };
    this.onQueueEmptySubscribers.push(subscriber);

    return () => {
      remove(this.onQueueEmptySubscribers, subscriber);
    };
  }

  notifyOnQueueEmpty(selectionPhotos: SelectionPhoto[]): void {
    for (const subscriber of this.onQueueEmptySubscribers) {
      setTimeout(() => subscriber.callback(selectionPhotos), 0);
    }
  }
}
